import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Bell, Send, Save, ShieldCheck, KeyRound, PlugZap, AlertTriangle, CheckCheck,
  Trash2, ChevronDown, Clock, Inbox, TriangleAlert,
} from "lucide-react";
import {
  DataTable, EmptyState, FilterSelect, MiniStat, PageHead, PageShell, Pagination,
  Pill, Row, SearchInput, StatStrip, Td, Toolbar, relativeTime, usePagedRows,
} from "@/components/ui/kit";

const kindMeta = {
  test: { icon: Bell, color: "var(--c-sky)", label: "Test", severity: "info" },
  session: { icon: KeyRound, color: "var(--c-amber)", label: "Session", severity: "warning" },
  reconnect: { icon: PlugZap, color: "var(--c-rose)", label: "Reconnect", severity: "critical" },
  job: { icon: AlertTriangle, color: "var(--c-rose)", label: "Job", severity: "critical" },
};

const SEVERITY = {
  critical: { tone: "var(--c-rose)", label: "Critical" },
  warning: { tone: "var(--c-amber)", label: "Warning" },
  info: { tone: "var(--c-sky)", label: "Info" },
};

const COLUMNS = [
  { key: "sev", label: "Severity" },
  { key: "type", label: "Type", hide: "hidden md:table-cell" },
  { key: "src", label: "Job / Account", hide: "hidden lg:table-cell" },
  { key: "msg", label: "Message" },
  { key: "time", label: "Time", align: "right", hide: "hidden md:table-cell" },
  { key: "state", label: "Status", align: "right" },
];

export default function Notifications() {
  const [cfg, setCfg] = useState({ enabled: false, chat_id: "", has_token: false });
  const [token, setToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [search, setSearch] = useState("");
  const [sevFilter, setSevFilter] = useState("all");
  const [readFilter, setReadFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const loadCfg = useCallback(async () => {
    const { data } = await api.get("/notifications");
    setCfg(data);
  }, []);

  const loadAlerts = useCallback(async () => {
    try {
      const { data } = await api.get("/alerts");
      setAlerts(data.alerts || []);
      setUnread(data.unread || 0);
    } catch (e) {
      console.error("alerts load failed", e);
    }
  }, []);

  useEffect(() => {
    loadCfg();
    loadAlerts();
    const t = setInterval(loadAlerts, 15000);
    return () => clearInterval(t);
  }, [loadCfg, loadAlerts]);

  const markRead = async () => {
    try {
      await api.post("/alerts/read");
      setUnread(0);
      setAlerts((a) => a.map((x) => ({ ...x, read: true })));
      window.dispatchEvent(new Event("clonecast:alerts-read"));
    } catch (e) {
      toast.error("Failed to mark alerts read");
    }
  };

  const clearAll = async () => {
    try {
      await api.delete("/alerts");
      setAlerts([]);
      setUnread(0);
      window.dispatchEvent(new Event("clonecast:alerts-read"));
      toast.success("Alerts cleared");
    } catch (e) {
      toast.error("Failed to clear alerts");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { enabled: cfg.enabled, chat_id: cfg.chat_id };
      if (token.trim()) payload.bot_token = token.trim();
      const { data } = await api.put("/notifications", payload);
      setCfg(data);
      setToken("");
      toast.success("Notification settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await api.post("/notifications/test", {});
      toast.success("Test message sent — check your Telegram");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send test");
    } finally {
      setTesting(false);
    }
  };

  const severityOf = (a) => (kindMeta[a.kind] || kindMeta.job).severity;

  const counts = useMemo(() => {
    const dayAgo = Date.now() - 86400000;
    return {
      critical: alerts.filter((a) => severityOf(a) === "critical").length,
      warning: alerts.filter((a) => severityOf(a) === "warning").length,
      unread,
      last24: alerts.filter((a) => new Date(a.ts).getTime() >= dayAgo).length,
    };
  }, [alerts, unread]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return alerts.filter((a) => {
      if (sevFilter !== "all" && severityOf(a) !== sevFilter) return false;
      if (readFilter === "unread" && a.read) return false;
      if (readFilter === "read" && !a.read) return false;
      if (!q) return true;
      return [a.message, a.job_name, a.account_label, a.kind]
        .filter(Boolean).some((s) => String(s).toLowerCase().includes(q));
    });
  }, [alerts, search, sevFilter, readFilter]);

  useEffect(() => { setPage(0); }, [search, sevFilter, readFilter, pageSize]);
  const paged = usePagedRows(filtered, { page, pageSize });

  return (
    <PageShell>
      <StatStrip cols="sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4">
        <MiniStat label="Critical" value={counts.critical} icon={TriangleAlert} accent="var(--c-rose)" tint="var(--t-rose)"
                  testid="alerts-critical" onClick={() => setSevFilter((c) => (c === "critical" ? "all" : "critical"))} active={sevFilter === "critical"} />
        <MiniStat label="Warnings" value={counts.warning} icon={AlertTriangle} accent="var(--c-amber)" tint="var(--t-amber)"
                  testid="alerts-warning" onClick={() => setSevFilter((c) => (c === "warning" ? "all" : "warning"))} active={sevFilter === "warning"} />
        <MiniStat label="Unread" value={counts.unread} icon={Inbox} accent="var(--c-sky)" tint="var(--t-sky)"
                  testid="alerts-unread-count" onClick={() => setReadFilter((c) => (c === "unread" ? "all" : "unread"))} active={readFilter === "unread"} />
        <MiniStat label="Last 24h" value={counts.last24} icon={Clock} accent="var(--c-violet)" tint="var(--t-violet)" testid="alerts-24h" />
      </StatStrip>

      <PageHead title="Alert History" count={filtered.length}>
        <Button size="sm" variant="outline" onClick={markRead} disabled={unread === 0}
                className="border-[var(--tg-border)] hover:bg-[var(--tg-hover)]" data-testid="mark-read-btn">
          <CheckCheck className="h-3.5 w-3.5 mr-1.5" /> Mark all read
        </Button>
        <Button size="sm" variant="outline" onClick={clearAll} disabled={alerts.length === 0}
                className="border-[var(--tg-border)] hover:bg-[var(--tg-hover)]" data-testid="clear-alerts-btn">
          <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Clear read
        </Button>
        <Button size="sm" variant="outline" onClick={() => setSettingsOpen((v) => !v)}
                className="border-[var(--tg-border)] hover:bg-[var(--tg-hover)]" data-testid="toggle-bot-settings">
          <Bell className="h-3.5 w-3.5 mr-1.5" /> Admin Bot
          <ChevronDown className={`h-3.5 w-3.5 ml-1.5 transition-transform ${settingsOpen ? "rotate-180" : ""}`} />
        </Button>
      </PageHead>

      {settingsOpen && (
        <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] p-4" data-testid="notifications-card">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_auto] gap-4 items-start">
            <div className="space-y-1.5">
              <Label className="label-caps">Bot Token</Label>
              <Input type="password" data-testid="input-bot-token" value={token} onChange={(e) => setToken(e.target.value)}
                     placeholder={cfg.has_token ? "•••••••• (saved — leave blank to keep)" : "123456:ABC-DEF..."}
                     className="bg-[var(--tg-bg)] border-[var(--tg-border)] h-9 focus:border-[var(--c-sky)] font-mono" />
              <p className="text-[11px] text-[var(--tg-faint)]">
                Create a bot with @BotFather and paste its token.{" "}
                {cfg.has_token && (
                  <span className="text-[var(--c-emerald)] inline-flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3" /> a token is saved
                  </span>
                )}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="label-caps">Admin Chat ID</Label>
              <Input data-testid="input-chat-id" value={cfg.chat_id} onChange={(e) => setCfg({ ...cfg, chat_id: e.target.value })}
                     placeholder="123456789" className="bg-[var(--tg-bg)] border-[var(--tg-border)] h-9 focus:border-[var(--c-sky)] font-mono" />
              <p className="text-[11px] text-[var(--tg-faint)]">Your numeric Telegram ID (or a group's negative ID).</p>
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Enabled</Label>
              <div className="flex items-center gap-2 h-9">
                <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="toggle-notifications" />
                <span className="text-xs text-[var(--tg-faint)]">{cfg.enabled ? "On" : "Off"}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={save} disabled={saving}
                        className="bg-[var(--c-sky)] hover:bg-[var(--c-sky-hover)] text-white font-semibold" data-testid="save-notifications-btn">
                  <Save className="h-3.5 w-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
                </Button>
                <Button size="sm" onClick={sendTest} disabled={testing || !cfg.has_token} variant="outline"
                        className="border-[var(--tg-border)] hover:bg-[var(--tg-hover)]" data-testid="test-notifications-btn">
                  <Send className="h-3.5 w-3.5 mr-1.5" /> {testing ? "Sending…" : "Test"}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      <Toolbar>
        <SearchInput value={search} onChange={setSearch} placeholder="Search messages, jobs, accounts…" testid="alerts-search" />
        <FilterSelect
          value={sevFilter} onValueChange={setSevFilter} testid="alerts-severity-filter"
          options={[
            { value: "all", label: "All severity" },
            { value: "critical", label: "Critical" },
            { value: "warning", label: "Warning" },
            { value: "info", label: "Info" },
          ]}
        />
        <FilterSelect
          value={readFilter} onValueChange={setReadFilter} width="w-32" testid="alerts-read-filter"
          options={[
            { value: "all", label: "All" },
            { value: "unread", label: "Unread" },
            { value: "read", label: "Read" },
          ]}
        />
      </Toolbar>

      <DataTable
        columns={COLUMNS}
        testid="alerts-panel"
        minWidth="min-w-[760px]"
        empty={
          paged.slice.length === 0 ? (
            <EmptyState
              icon={Bell}
              testid="no-alerts"
              title={alerts.length === 0 ? "No alerts yet" : "No alerts match these filters"}
              hint={alerts.length === 0 ? "Bot tests and operational failures appear here and on your Telegram bot." : undefined}
            />
          ) : null
        }
      >
        {paged.slice.map((a) => {
          const meta = kindMeta[a.kind] || kindMeta.job;
          const sev = SEVERITY[meta.severity];
          const Icon = meta.icon;
          return (
            <Row key={a.id} testid={`alert-${a.id}`} tone={!a.read ? sev.tone : undefined}>
              <Td>
                <Pill tone={sev.tone} dot={!a.read}>{sev.label}</Pill>
              </Td>
              <Td className="hidden md:table-cell">
                <span className="inline-flex items-center gap-1.5 text-xs text-[var(--tg-muted)]">
                  <Icon className="h-3.5 w-3.5" style={{ color: meta.color }} /> {meta.label}
                </span>
              </Td>
              <Td className="hidden lg:table-cell text-xs">
                <div className="truncate max-w-[160px] font-medium" title={a.job_name || a.account_label || "—"}>
                  {a.job_name || a.account_label || "—"}
                </div>
              </Td>
              <Td>
                <button
                  type="button"
                  onClick={() => setDetail(a)}
                  title="View details"
                  className="text-left text-sm text-[var(--tg-muted)] truncate max-w-[420px] block hover:text-[var(--tg-ink)] transition-colors"
                  data-testid={`alert-message-${a.id}`}
                >
                  {a.message}
                </button>
              </Td>
              <Td align="right" className="hidden md:table-cell text-[11px] text-[var(--tg-faint)] whitespace-nowrap"
                  title={new Date(a.ts).toLocaleString()}>
                {relativeTime(a.ts) || "—"}
              </Td>
              <Td align="right">
                {a.read
                  ? <span className="text-[11px] text-[var(--tg-faint)]">Read</span>
                  : <Pill tone="var(--c-sky)" size="sm">New</Pill>}
              </Td>
            </Row>
          );
        })}
      </DataTable>

      {filtered.length > pageSize && (
        <Pagination
          page={paged.safePage} pageCount={paged.pageCount} total={paged.total}
          from={paged.from} to={paged.to} pageSize={pageSize} onPageSize={setPageSize}
          onPrev={() => setPage((p) => Math.max(0, p - 1))}
          onNext={() => setPage((p) => Math.min(paged.pageCount - 1, p + 1))}
          testid="alerts-pagination"
        />
      )}

      {detail && (
        <div className="fixed inset-0 z-[80] bg-black/70 grid place-items-center p-6" onClick={() => setDetail(null)}>
          <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] p-5 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <Pill tone={SEVERITY[(kindMeta[detail.kind] || kindMeta.job).severity].tone}>
                {SEVERITY[(kindMeta[detail.kind] || kindMeta.job).severity].label}
              </Pill>
              <span className="text-sm font-semibold truncate">{detail.job_name || detail.account_label || "CloneCast"}</span>
            </div>
            <p className="text-sm text-[var(--tg-muted)] break-words whitespace-pre-wrap">{detail.message}</p>
            <div className="text-[11px] text-[var(--tg-faint)] mt-3">{new Date(detail.ts).toLocaleString()}</div>
            <Button size="sm" variant="outline" onClick={() => setDetail(null)}
                    className="mt-4 border-[var(--tg-border)] hover:bg-[var(--tg-hover)]">Close</Button>
          </Card>
        </div>
      )}
    </PageShell>
  );
}
